import { useContext, useEffect, useState } from 'react'
import { Col, Row, Modal, Button } from 'react-bootstrap'
import FeatureGate from '../../components/FeatureGate'
import { FEATURE_KEYS } from '../../features/featureRegistry'
import roleContext from '../../context/roleContext'
import { useAuth } from '../../modules/auth'
import { RoleContextValue } from '../../types/OtherTypes'
import IncidentList from './IncidentList'
import IncidentForm from './IncidentForm'
import IncidentDetail from './IncidentDetail'
import PoshCommitteeForm from './PoshCommitteeForm'
import PoshCommitteeList from './PoshCommitteeList'
import CommitteeDisplay from './CommitteeDisplay'
import IncidentCategoryManagement from './IncidentCategoryManagement'
import { getIncidentStats } from '../../../api/IncidentAPI'
import { getUnreadEmployeeCommunicationCountAPI } from '../../../api/NotificationAPI'
import { useLocation, useNavigate } from 'react-router-dom'

const Incident = () => {
  const { currentUser } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  let roleState: RoleContextValue = useContext(roleContext)
  // const roleName = roleState.roleName?.toLowerCase()

  // const isSystemAdmin = currentUser?.email?.toLowerCase() === 'admin@ctrine.com' ||
  //   currentUser?.email?.toLowerCase() === 'admin@ctrine.trine'
  // const isSuperAdmin = roleState.roleName === 'Super Admin' ||
  //   roleState.roleName === 'SUPER_ADMIN' ||
  //   roleState.features.includes('super admin')
  
  // const isAdmin = roleName === 'admin' ||
  //   roleState.features.some(f => f.toLowerCase() === 'admin')

  // const canManage = isSuperAdmin || isSystemAdmin || isAdmin
  const normalizedRoleName = roleState.roleName
  ?.trim()
  .replace(/[_-]+/g, ' ')
  .replace(/([a-z])([A-Z])/g, '$1 $2')
  .replace(/\s+/g, ' ')
  .toUpperCase() || ''

const isSystemAdmin =
  currentUser?.email?.toLowerCase() === 'admin@ctrine.com' ||
  currentUser?.email?.toLowerCase() === 'admin@ctrine.trine'

const isSuperAdmin =
  normalizedRoleName === 'SUPER ADMIN' ||
  normalizedRoleName === 'SUPERADMIN'

const isAdmin =
  normalizedRoleName === 'ADMIN'

const isHrRole = normalizedRoleName === 'HR' || normalizedRoleName.startsWith('HR ')
const isPoshMember = normalizedRoleName === 'POSH COMMITTEE MEMBER' || Boolean(currentUser?.isPoshCommitteeMember)
const canManage =
  isSuperAdmin ||
  isSystemAdmin ||
  isAdmin ||
  isHrRole ||
  isPoshMember
const canAdminister = isSuperAdmin || isSystemAdmin || isAdmin

  const [APIStatus, setAPIStatus] = useState({
    loading: false,
    error: null,
    submitLoading: false,
  })

  const [stats, setStats] = useState({
    totalIncidents: 0,
    openIncidents: 0,
    closedIncidents: 0,
    byStatus: [],
    byCategory: [],
    byPriority: [],
  })

  const [unreadEmployeeCommCount, setUnreadEmployeeCommCount] = useState(0)

  const [showIncidentModal, setShowIncidentModal] = useState(false)

  const [refreshTrigger, setRefreshTrigger] = useState(0)

  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = sessionStorage.getItem('incidentActiveTab')
    if (savedTab) return savedTab
    return 'list'
  })

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get('tab') === 'create') {
      setActiveTab('create')
    }
    if (params.get('tab') === 'detail') {
      setActiveTab('detail')
    }
  }, [location])

  const handleTabChange = (tab: string) => {
    setActiveTab(tab)
    sessionStorage.setItem('incidentActiveTab', tab)
    navigate('/incident')
    if (tab === 'list') {
      setRefreshTrigger(prev => prev + 1)
    }
  }

  useEffect(() => {
    let mounted = true
    if (mounted && canAdminister) {
      fetchIncidentStats()
    }
    return () => {
      mounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAdminister])

  useEffect(() => {
    let mounted = true
    if (mounted && currentUser?.id) {
      fetchUnreadEmployeeCommCount()
    }
    return () => {
      mounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id])

  const fetchIncidentStats = async () => {
    setAPIStatus(prev => ({ ...prev, loading: true }))
    try {
      const response = await getIncidentStats()
      if (response.statusCode === 200) {
        setStats(response.data)
      }
    } catch (error) {
      console.error('Error fetching incident stats:', error)
    } finally {
      setAPIStatus(prev => ({ ...prev, loading: false }))
    }
  }

  const fetchUnreadEmployeeCommCount = async () => {
    try {
      const response = await getUnreadEmployeeCommunicationCountAPI(currentUser?.id || '')
      if (response.success && response.data) {
        setUnreadEmployeeCommCount(response.data.unreadCount || 0)
      }
    } catch (error) {
      console.error('Error fetching unread employee communication count:', error)
    }
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'list':
        return <IncidentList onTabChange={handleTabChange} refreshTrigger={refreshTrigger} />
      case 'create':
        return <IncidentForm onTabChange={handleTabChange} />
      case 'detail':
        return <IncidentDetail onTabChange={handleTabChange} />
      case 'posh-committee':
        return <PoshCommitteeForm onTabChange={handleTabChange} />
      case 'posh-committee-list':
        return <PoshCommitteeList onTabChange={handleTabChange} />
      case 'committee':
        return <CommitteeDisplay />
      case 'category-management':
        return <IncidentCategoryManagement onTabChange={handleTabChange} />
      default:
        return <IncidentList onTabChange={handleTabChange} refreshTrigger={refreshTrigger} />
    }
  }

  return (
    <div className="incident-page">
        <Row>
          <Col md={12}>
            <div className="card rounded-3 border-0 shadow-sm bg-body">
              <div className="p-4 border-bottom border-secondary-subtle">
                  <div className="d-flex justify-content-between align-items-center">
                  <h3 className="mb-0 fw-bold text-body">Concern Management</h3>
                  <div className="d-flex gap-2 align-items-center">
                    {canManage ? (
                      <button
                        className={`btn btn-sm rounded-2 py-2 px-3 ${
                          activeTab === 'list' ? 'btn-primary' : 'btn-outline-secondary'
                        }`}
                        onClick={() => handleTabChange('list')}
                      >
                        {canAdminister ? 'All Concerns' : isHrRole || isPoshMember ? 'Assigned Concerns' : 'My Concerns'}
                      </button>
                    ) : (
                      <button
                        className={`btn btn-sm rounded-2 py-2 px-3 position-relative ${
                          activeTab === 'list' ? 'btn-primary' : 'btn-outline-secondary'
                        }`}
                        onClick={() => handleTabChange('list')}
                      >
                        My Concerns
                        {unreadEmployeeCommCount > 0 && (
                          <span
                            className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                            style={{
                              fontSize: '10px',
                              padding: '4px 6px',
                              minWidth: '18px',
                              height: '18px',
                            }}
                          >
                            {unreadEmployeeCommCount}
                          </span>
                        )}
                      </button>
                    )}
                    <button
                      className="btn btn-sm rounded-2 py-2 px-3 btn-primary"
                      onClick={() => setShowIncidentModal(true)}
                    >
                      Raise a Concern
                    </button>
                    {!canManage && (
                      <button
                        className={`btn btn-sm rounded-2 py-2 px-3 ms-2 ${
                          activeTab === 'committee'
                            ? 'btn-primary'
                            : 'btn-outline-secondary'
                        }`}
                        onClick={() => handleTabChange('committee')}
                      >
                        Committees
                      </button>
                    )}
                    {canAdminister && (
                      <>
                        <button
                          className={`btn btn-sm rounded-2 py-2 px-3 ms-2 ${activeTab === 'posh-committee-list' ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() => handleTabChange('posh-committee-list')}
                        >
                          Manage Committees
                        </button>
                        {/* <button
                          className="btn btn-sm rounded-2 py-2 px-3 ms-2 btn-primary"
                          onClick={() => handleTabChange('posh-committee')}
                        >
                          Create POSH Committee
                        </button> */}
                        <button
                          className={`btn btn-sm rounded-2 py-2 px-3 ms-2 ${activeTab === 'category-management' ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() => handleTabChange('category-management')}
                        >
                          Manage Categories
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="p-4">
                {canAdminister && (
                  <Row className="mb-4 g-3">
                    <Col md={3}>
                      <div className="card rounded-3 border-0 shadow-sm p-4 h-100 bg-body">
                        <div className="d-flex align-items-center gap-3">
                          <div className="rounded-2 d-flex align-items-center justify-content-center bg-primary bg-opacity-10" style={{ width: '48px', height: '48px' }}>
                            <span className="fw-bold text-primary" style={{ fontSize: '1.25rem' }}>📊</span>
                          </div>
                          <div>
                            <h6 className="mb-1 text-muted" style={{ fontSize: '0.875rem' }}>Total Incidents</h6>
                            <h3 className="mb-0 fw-bold text-body" style={{ fontSize: '1.75rem' }}>{stats.totalIncidents}</h3>
                          </div>
                        </div>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="card rounded-3 border-0 shadow-sm p-4 h-100 bg-body">
                        <div className="d-flex align-items-center gap-3">
                          <div className="rounded-2 d-flex align-items-center justify-content-center bg-warning bg-opacity-10" style={{ width: '48px', height: '48px' }}>
                            <span className="fw-bold text-warning" style={{ fontSize: '1.25rem' }}>📋</span>
                          </div>
                          <div>
                            <h6 className="mb-1 text-muted" style={{ fontSize: '0.875rem' }}>Open Incidents</h6>
                            <h3 className="mb-0 fw-bold text-body" style={{ fontSize: '1.75rem' }}>{stats.openIncidents}</h3>
                          </div>
                        </div>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="card rounded-3 border-0 shadow-sm p-4 h-100 bg-body">
                        <div className="d-flex align-items-center gap-3">
                          <div className="rounded-2 d-flex align-items-center justify-content-center bg-success bg-opacity-10" style={{ width: '48px', height: '48px' }}>
                            <span className="fw-bold text-success" style={{ fontSize: '1.25rem' }}>✅</span>
                          </div>
                          <div>
                            <h6 className="mb-1 text-muted" style={{ fontSize: '0.875rem' }}>Closed Incidents</h6>
                            <h3 className="mb-0 fw-bold text-body" style={{ fontSize: '1.75rem' }}>{stats.closedIncidents}</h3>
                          </div>
                        </div>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="card rounded-3 border-0 shadow-sm p-4 h-100 bg-body">
                        <div className="d-flex align-items-center gap-3">
                          <div className="rounded-2 d-flex align-items-center justify-content-center bg-info bg-opacity-10" style={{ width: '48px', height: '48px' }}>
                            <span className="fw-bold text-info" style={{ fontSize: '1.25rem' }}>📈</span>
                          </div>
                          <div>
                            <h6 className="mb-1 text-muted" style={{ fontSize: '0.875rem' }}>Resolution Rate</h6>
                            <h3 className="mb-0 fw-bold text-body" style={{ fontSize: '1.75rem' }}>
                              {stats.totalIncidents > 0
                                ? Math.round((stats.closedIncidents / stats.totalIncidents) * 100)
                                : 0}
                              %
                            </h3>
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                )}
                {renderTabContent()}
              </div>
            </div>
          </Col>
        </Row>

        {/* Raise Incident Modal */}
        <Modal
          show={showIncidentModal}
          onHide={() => setShowIncidentModal(false)}
          size="lg"
          centered
          dialogClassName="incident-modal"
        >
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title className="fw-bold">Raise a Concern</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <IncidentForm 
              onTabChange={() => setShowIncidentModal(false)} 
              onSuccess={() => setRefreshTrigger(prev => prev + 1)}
            />
          </Modal.Body>
        </Modal>
      </div>
  )
}

export default Incident
